import {
  Typography,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  Snackbar,
  LinearProgress,
  Modal,
  IconButton,
} from "@mui/material";
import { AddIcon, CloseIcon, ExpandMoreIcon, extractPathsByKey, getDatasetInfo, getFile, SearchIcon } from "common-lib";
import { useState, useEffect, useMemo, Fragment, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useRecoilValue, useRecoilState, useResetRecoilState } from "recoil";
import {
  projectInfoState,
  openDefectPreviewState,
  parentModelState,
  continueTrainState,
  selectedInspModelState,
} from "../../recoil";
import { validListStateAtom, myAIModuleState, validatingStateAtom, continueTrainInfoState } from "../../recoil/upgrade";
import { fetchProjectInspModels } from "../../services/inspModelCache";
import { formatInspResult, parseStringToArray } from "../../utils/utils";
import TreeModel from "./TreeModel";
import { Rnd } from "react-rnd";
import { useEpochList } from "../../hooks/useEpochList";
import { useHistoryMap } from "../../hooks/useHistoryMap";
import { modelThreshold, useParsedModels } from "../../hooks/useParsedModels";
import { InfoItem, InspModelItem } from "@types";
import { addInspRule } from "../../services/inspRuleService";
import { t } from "i18next";
import { Info } from "lucide-react";
import { InspRuleFlowExplanationDiagram } from "common_ui_lib";

export default function InspRuleOption({
  thresholdCustom,
  setThresholdCustom,
  inspModelList: inspModelListProp,
}: {
  thresholdCustom: number;
  setThresholdCustom: (th: number) => void;
  inspModelList?: InspModelItem[]; // 부모 목록 동기화 (모델 비교 저장 시 즉시 반영)
}) {
  const { id } = useParams();
  const projectData = useRecoilValue(projectInfoState);
  const [ruleName, setRuleName] = useState("");
  const [recipe, setRecipe] = useState("");
  const [fileName, setFileName] = useState("");

  const [inspModelList, setInspModelList] = useState<InspModelItem[]>([]);
  const [selectedInspModel, setSelectedInspModel] = useRecoilState(selectedInspModelState);
  const [continueTrainInfo, setContinueTrainInfo] = useRecoilState(continueTrainInfoState);

  const [validList, setValidList] = useRecoilState(validListStateAtom);
  const [validatingState, setValidatingState] = useRecoilState(validatingStateAtom);
  const [myAIModule, setMyAIModule] = useRecoilState(myAIModuleState);
  const [openDefectPreview, setOpenDefectPreview] = useRecoilState(openDefectPreviewState);
  const [parentModel, setParentModel] = useRecoilState(parentModelState);
  const [continueTrain, setContinueTrain] = useRecoilState(continueTrainState);
  const [openHelpInspRule, setOpenHelpInspRule] = useState(false);

  const [openTree, setOpenTree] = useState(false);
  const [info, setInfo] = useState<InfoItem>({
    performance: {
      overkill: 0,
      missing: 0,
      threshold: 0.0,
    },
  });

  const fetchInspModel = async () => {
    try {
      setInspModelList(await fetchProjectInspModels(Number(id)));
    } catch (error: any) {
      console.error(error);
    }
  };

  const prevValidatingRef = useRef(validatingState[Number(id)]);

  useEffect(() => {
    const prev = prevValidatingRef.current;
    const curr = validatingState[Number(id)];
    prevValidatingRef.current = curr;

    if (prev === true && curr === false) {
      fetchInspModel();
    }
  }, [validatingState]);

  useEffect(() => {
    fetchInspModel();
  }, []);

  useEffect(() => {
    if (inspModelListProp) setInspModelList(inspModelListProp);
  }, [inspModelListProp]);

  const parsedModels = useParsedModels(inspModelList);
  const historyMap = useHistoryMap(parsedModels);
  const epochList = useEpochList(parsedModels);

  // 프로젝트가 바뀌면 이전 선택 초기화 → 아래 effect가 같은 렌더에서 이어 실행되어 다시 탐색
  useEffect(() => {
    setSelectedInspModel(null);
  }, [projectData]);

  useEffect(() => {
    const fetchData = async () => {
      if (continueTrainInfo.trainHistory_id && continueTrainInfo.epoch) {
        const found = parsedModels.find(
          (m) =>
            m.trainHistory_id === continueTrainInfo.trainHistory_id &&
            m.epoch?.current === continueTrainInfo.epoch.current,
        );

        if (found) {
          const info = found.info ? JSON.parse(found.info) : null;
          if (info) setInfo(info);

          if (selectedInspModel?.id !== found.id) {
            setSelectedInspModel(found);
            const th = modelThreshold(info);
            if (th !== null) setThresholdCustom(th);
          }

          const historyIndex = historyMap.find((h) => h.trainHistory_id === continueTrainInfo.trainHistory_id)?.round;
          const ruleName = `${historyIndex}round-${continueTrainInfo.epoch.current}-rule`;
          setRuleName(ruleName);
        }
      }
    };
    fetchData();
  }, [projectData, continueTrainInfo.epoch, parsedModels]);

  const handleSubmitInspRule = async () => {
    if (recipe.trim() === "" || fileName.trim() === "") {
      toast.warn(t("RecipeFileNameRequiredMsg"));
      return;
    }

    if (!selectedInspModel) {
      toast.warn(t("SelectModelMsg"));
      return;
    }

    try {
      const response = await addInspRule({
        description: "",
        name: ruleName,
        model_uuid: selectedInspModel.uuid,
        recipe_regex: parseStringToArray(recipe),
        image_regex: parseStringToArray(fileName),
      });

      if (response) {
        toast.success(t("AddInspRuleSuccessMsg"));
      }
    } catch (error) {
      console.error(error);
      toast.error(t("AddInspRuleFailMsg"));
    }
  };

  const isFormValid = ruleName.trim().length > 0 && recipe.trim().length > 0 && fileName.trim().length > 0;

  return (
    <>
      {projectData?.step == "검사" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "8px 16px",
            backgroundColor: "var(--dk-surface, #fff)",
          }}
        >
          <Box sx={{ display: "flex", mb: 1, alignItems: "center" }}>
            <Typography component="span" fontWeight={700}>
              {t("InspectionRule")}
            </Typography>
            <IconButton onClick={() => setOpenHelpInspRule(true)}>
              <Info size={16} />
            </IconButton>
          </Box>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                id="rule-name-input"
                label={t("Name")}
                placeholder={t("InspRuleName")}
                size="small"
                value={ruleName}
                onChange={(e) => setRuleName(e.target.value)}
              />
              <TextField
                id="rule-recipe-input"
                label={t("Recipe")}
                placeholder={t("MaterialNameExamplePlaceholder")}
                size="small"
                value={recipe}
                onChange={(e) => setRecipe(e.target.value)}
              />
              <TextField
                id="rule-file-input"
                label={t("FileName")}
                size="small"
                placeholder={t("ImageRuleExamplePlaceholder")}
                variant="outlined"
                sx={{ minWidth: 200 }}
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
              />
              <Button
                id="add-rule-btn"
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  backgroundColor: isFormValid ? "var(--basic-color)" : "#ccc",
                  color: "var(--dk-text-2, #4b5563)",
                  "&:hover": {
                    backgroundColor: isFormValid ? "var(--dash-btn-border)" : "#ccc",
                    opacity: isFormValid ? 0.9 : 1,
                  },
                }}
                onClick={handleSubmitInspRule}
              >
                {t("AddRule")}
              </Button>
            </Box>
          </div>
        </Box>
      )}
      {openTree && (
        <Modal
          open={openTree}
          sx={{ pointerEvents: "none" }}
          onClose={() => setOpenTree(false)}
          disableEnforceFocus
          disableAutoFocus
          hideBackdrop
          disableRestoreFocus
        >
          <div>
            <Rnd
              default={{ x: 200, y: 100, width: 900, height: 600 }}
              minWidth={600}
              minHeight={400}
              bounds="window"
              dragHandleClassName="drag-handle"
              style={{
                pointerEvents: "auto",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  borderRadius: 2,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Box
                  className="drag-handle"
                  sx={{
                    p: 2,
                    cursor: "move",
                    borderBottom: "1px solid var(--dk-border, #ddd)",
                    padding: "8px 16px",
                    fontWeight: 600,
                  }}
                >
                  <Typography variant="h5" fontWeight={700} sx={{ display: "flex", alignItems: "center" }}>
                    {t("Histogram")}
                  </Typography>

                  <IconButton
                    onClick={() => {
                      setOpenTree(false);
                    }}
                    sx={{ position: "absolute", right: 8, top: 8 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
                  <TreeModel />
                </Box>
              </Box>
            </Rnd>
          </div>
        </Modal>
      )}
      {openHelpInspRule && (
        <InspRuleFlowExplanationDiagram
          open={openHelpInspRule}
          onClose={() => {
            setOpenHelpInspRule(false);
          }}
        />
      )}
    </>
  );
}
